import React, { useRef, useEffect, useState } from "react";
import { ISourceType } from "../../models/LeadData.model";

interface SourceTypeDropdownProps {
  currentType: ISourceType | "";
  onUpdate: (newType: ISourceType) => void;
}

const SourceTypeDropdown: React.FC<SourceTypeDropdownProps> = ({
  currentType,
  onUpdate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<ISourceType | "">(
    currentType
  );
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedType(currentType);
  }, [currentType]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getSourceTypeColor = (type: ISourceType) => {
    switch (type) {
      case ISourceType.Hot:
        return "text-red-500";
      case ISourceType.Cold:
        return "text-green-500";
      case ISourceType.Warm:
        return "text-yellow-500";
      default:
        return "text-gray-500";
    }
  };

  const handleUpdate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedType) {
      onUpdate(selectedType);
      setIsOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative inline-block">
      <div
        className="flex items-center shadow-md px-3 py-1 rounded-2xl cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
      >
        <span
          className={`text-[10px] ${getSourceTypeColor(
            selectedType as ISourceType
          )}`}
        >
          ●
        </span>
        <p className="text-[12px] font-semibold text-gray-800 ml-1">
          {selectedType || "Select"}
        </p>
        <svg
          className={`w-4 h-4 text-gray-600 ml-1 transform transition-transform ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {isOpen && (
        <div
          className="absolute z-999 -top-20 -right-40 bg-white border border-gray-200 shadow-md text-xs rounded-lg p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col space-y-3">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as ISourceType)}
              className="p-1 text-xs rounded-lg border border-gray-300 text-gray-700"
            >
              <option value="" disabled>
                Select Source Type
              </option>
              {Object.values(ISourceType).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <button
              onClick={handleUpdate}
              className="bg-blue-500 text-white px-4 py-1 text-xs rounded-lg shadow hover:bg-blue-600"
            >
              Update
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SourceTypeDropdown;
